import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Mail, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

interface LoginFormProps {
  onSuccess?: () => void;
  title?: string;
  subtitle?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  title = 'Sign In',
  subtitle = 'Access your dashboard'
}) => {
  const { login, changePassword, user, logout } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        if (result.requiresPasswordChange) {
          setRequiresPasswordChange(true);
        } else {
          // Auto-redirect based on user role
          handleSuccessfulLogin();
        }
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessfulLogin = () => {
    // If onSuccess callback is provided, use it (for custom handling)
    if (onSuccess) {
      onSuccess();
      return;
    }

    // Show redirecting state
    setIsRedirecting(true);

    // Auto-redirect based on user role
    // Use a small delay to ensure the auth context is updated
    setTimeout(() => {
      const userData = JSON.parse(localStorage.getItem('authUser') || '{}');
      console.log('Redirecting user with role:', userData.role);
      
      if (userData.role === 'admin') {
        window.location.href = '/admin';
      } else if (userData.role === 'doctor') {
        window.location.href = '/doctor';
      } else {
        // Fallback to home page
        window.location.href = '/';
      }
    }, 100);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await changePassword(formData.newPassword);
      
      if (result.success) {
        setRequiresPasswordChange(false);
        // Auto-redirect after password change
        handleSuccessfulLogin();
      } else {
        setError(result.error || 'Password change failed');
      }
    } catch (error) {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Common input field styles
  const inputFieldClass = "w-full px-4 py-3 border border-luma-border rounded-xl focus:ring-2 focus:ring-luma-primary focus:border-luma-primary transition-all duration-300 text-base bg-white";

  // If user is already logged in, show dashboard options
  if (user && !requiresPasswordChange) {
    const dashboardUrl = user.role === 'admin' ? '/admin' : user.role === 'doctor' ? '/doctor' : '/';
    
    return (
      <div className="min-h-screen gradient-pastel flex items-center justify-center p-4">
        <div className="card max-w-lg w-full text-center">
          <div className="mb-8">
            <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-soft">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-luma-text mb-2">Already Signed In</h1>
            <p className="text-luma-text-light mb-4">
              You are currently signed in as <strong>{user.name || user.email}</strong>
            </p>
            <p className="text-sm text-gray-500">
              Role: {user.role === 'admin' ? 'Administrator' : 'Doctor'}
            </p>
          </div>

          <div className="space-y-4">
            <a
              href={dashboardUrl}
              className="btn-primary w-full py-3 text-base font-medium block"
            >
              Go to Dashboard
            </a>
            
            <button
              onClick={() => {
                logout();
                // logout() now handles redirect to /login
              }}
              className="btn-secondary w-full py-3 text-base font-medium"
            >
              Sign Out & Login as Different User
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (requiresPasswordChange) {
    return (
      <div className="min-h-screen gradient-pastel flex items-center justify-center p-4">
        <div className="card max-w-lg w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-soft">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-luma-text mb-2">Change Password</h1>
            <p className="text-luma-text-light">Please set a new password to continue</p>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-luma-text mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className={`${inputFieldClass} pr-12`}
                  placeholder="Enter new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-luma-text-light hover:text-luma-text"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-luma-text mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`${inputFieldClass} pr-12`}
                  placeholder="Confirm new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-luma-text-light hover:text-luma-text"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-3 text-base font-medium"
            >
              {isLoading ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-pastel flex items-center justify-center p-4">
      <div className="card max-w-lg w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-soft">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-luma-text mb-2">{title}</h1>
          <p className="text-luma-text-light">
            {subtitle}
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-luma-text mb-2">
              Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`${inputFieldClass} pl-12`}
                placeholder="Enter your email"
                required
              />
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-luma-text-light" />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-luma-text mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`${inputFieldClass} pl-12 pr-12`}
                placeholder="Enter your password"
                required
              />
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-luma-text-light" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-luma-text-light hover:text-luma-text"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || isRedirecting}
            className="btn-primary w-full py-3 text-base font-medium"
          >
            {isLoading ? 'Signing In...' : isRedirecting ? 'Redirecting...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-luma-text-light">
            Need help? Contact your administrator
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;